import { Box, Button, Typography, Stack } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { Sparkles, Play } from 'lucide-react';
import { useTour } from './TourProvider';

const breathe = (color: string) => keyframes`
  0%, 100% { box-shadow: 0 18px 50px ${alpha(color, 0.35)}, 0 0 0 0 ${alpha(color, 0.45)}; }
  50%      { box-shadow: 0 18px 50px ${alpha(color, 0.45)}, 0 0 0 8px ${alpha(color, 0.0)}; }
`;

/**
 * Floating "Start guided demo" button — always visible when the tour is closed.
 * Clicking opens the overlay and starts autoplay (the booth happy path).
 */
export default function TourLauncher() {
  const { isOpen, open, play, stepIndex, highlightIndex } = useTour();
  const theme = useTheme();

  if (isOpen) return null;

  const accent = theme.palette.primary.main;
  const softAccent = theme.palette.primary.light;
  const hasProgress = stepIndex > 0 || highlightIndex > 0;

  return (
    <Box sx={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1500 }}>
      <Button
        onClick={() => {
          open();
          play();
        }}
        sx={{
          bgcolor: '#0A1612',
          color: '#fff',
          pl: 1.25,
          pr: 2.25,
          py: 1.25,
          borderRadius: 999,
          border: `1px solid ${alpha(softAccent, 0.35)}`,
          textTransform: 'none',
          animation: `${breathe(accent)} 2.8s ease-in-out infinite`,
          '&:hover': { bgcolor: '#14241f', borderColor: softAccent },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Play size={14} fill="#fff" color="#fff" style={{ marginLeft: 2 }} />
          </Box>
          <Stack spacing={0} alignItems="flex-start">
            <Typography
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: 2,
                color: softAccent,
                textTransform: 'uppercase',
                lineHeight: 1,
              }}
            >
              <Sparkles size={9} style={{ marginRight: 3, verticalAlign: -1 }} />
              Autoplay demo
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              {hasProgress ? 'Resume guided tour' : 'See ClearCollect in 3 minutes'}
            </Typography>
          </Stack>
        </Stack>
      </Button>
    </Box>
  );
}
